import LoadingButton from '@app/src/components/LoadingButton'
import { useMenuState } from '@app/src/hooks/mui-hooks'
import { ArrowDropDown, AutoAwesomeOutlined } from '@mui/icons-material'
import { Button, ButtonGroup, ListItemText, Menu, MenuItem } from '@mui/material'
import type { FC } from 'react'
import React, { useRef } from 'react'
import { useIntl } from 'react-intl'
import { SuggestionSource } from './suggestionSources'

const SOURCE_LABEL_IDS: Record<SuggestionSource, string> = {
  [SuggestionSource.PreviousAnswer]: 'suggestedAnswers.previousAnswerSource',
  [SuggestionSource.PublicData]: 'suggestedAnswers.publicData',
  [SuggestionSource.Document]: 'suggestedAnswers.suggestion',
}

const pillStyles = {
  backgroundColor: 'communication.light',
  borderColor: 'communication.main',
  color: 'communication.dark',
  '&:hover': {
    backgroundColor: 'communication.main',
    borderColor: 'communication.main',
  },
  '&.MuiLoadingButton-loading, &.Mui-disabled': {
    backgroundColor: 'communication.light',
    color: 'communication.dark',
  },
}

type SuggestAnswerButtonProps = {
  sources: SuggestionSource[]
  isLoadingSuggestions?: boolean
  isApplying?: boolean
  onSelectSource: (source: SuggestionSource) => void
}

const SuggestAnswerButton: FC<SuggestAnswerButtonProps> = ({
  sources,
  isLoadingSuggestions,
  isApplying,
  onSelectSource,
}) => {
  const { formatMessage } = useIntl()
  const [isMenuOpen, , openMenu, closeMenu] = useMenuState()
  const buttonGroupRef = useRef<HTMLDivElement>(null)

  const isWaitingForFirstSource = Boolean(isLoadingSuggestions) && !sources.length

  if (!sources.length && !isLoadingSuggestions) return null

  const label = formatMessage({ id: 'suggestedAnswers.viewSuggestions' })

  if (sources.length < 2) {
    return (
      <LoadingButton
        size="small"
        variant="contained"
        loading={isWaitingForFirstSource || Boolean(isApplying)}
        loaderColor="communication.dark"
        startIcon={<AutoAwesomeOutlined />}
        onClick={() => onSelectSource(sources[0])}
        sx={pillStyles}
      >
        {label}
      </LoadingButton>
    )
  }

  return (
    <>
      <ButtonGroup
        ref={buttonGroupRef}
        size="small"
        variant="contained"
        disableElevation
        sx={{ '& .MuiButtonGroup-grouped': pillStyles }}
      >
        <Button startIcon={<AutoAwesomeOutlined />} onClick={openMenu}>
          {label}
        </Button>
        <Button onClick={openMenu} sx={{ px: 0.5, minWidth: 'auto' }}>
          <ArrowDropDown />
        </Button>
      </ButtonGroup>
      <Menu
        anchorEl={buttonGroupRef.current}
        open={isMenuOpen}
        onClose={closeMenu}
        slotProps={{ paper: { sx: { minWidth: buttonGroupRef.current?.offsetWidth } } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {sources.map(source => (
          <MenuItem
            key={source}
            onClick={() => {
              closeMenu()
              onSelectSource(source)
            }}
          >
            <ListItemText>{formatMessage({ id: SOURCE_LABEL_IDS[source] })}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default SuggestAnswerButton
